import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronUp, Users, Info, AlertCircle, Bell, BellOff, Volume2, VolumeX, Goal, Square, Clock, Flag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlayerCard = ({ player, teamKey, index, isExpanded, onToggle }) => {
  const hasFacts = player.facts && player.facts.length > 0;

  return (
    <div
      className={`bg-slate-800/50 rounded-lg overflow-hidden transition-all ${hasFacts ? 'cursor-pointer hover:bg-slate-800/70' : ''}`}
      onClick={() => hasFacts && onToggle()}
      data-testid={`player-card-${teamKey}-${index}`}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-sm">
            {player.number}
          </div>
          <div>
            <p className="text-white font-medium">{player.name}</p>
            {player.position && (
              <p className="text-slate-400 text-xs">{player.position}</p>
            )}
          </div>
        </div>
        {hasFacts && (
          <div className="flex items-center gap-1 text-blue-400">
            <Info className="w-4 h-4" />
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        )}
      </div>
      {isExpanded && hasFacts && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-700/50">
          <ul className="space-y-1">
            {player.facts.map((fact, i) => (
              <li key={`pf-${i}-${fact.slice(0, 20)}`} className="text-slate-300 text-sm flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">•</span>
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const TeamSection = ({ team, teamKey, teamName, expandedItems, onToggle }) => {
  const teamFactsKey = `${teamKey}-facts`;
  const isTeamFactsExpanded = expandedItems[teamFactsKey];
  const hasTeamFacts = team.facts && team.facts.length > 0;
  const starting = team.starting || [];
  const substitutes = team.substitutes || [];

  return (
    <div className="space-y-4">
      <div
        className={`bg-gradient-to-r from-slate-800 to-slate-800/50 rounded-lg p-4 ${hasTeamFacts ? 'cursor-pointer hover:from-slate-700/80' : ''}`}
        onClick={() => hasTeamFacts && onToggle(teamFactsKey)}
        data-testid={`team-header-${teamKey}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-slate-400" />
            <div>
              <h2 className="text-xl font-bold text-white">{teamName}</h2>
              {team.manager && (
                <p className="text-slate-400 text-sm">Manager: {team.manager}</p>
              )}
            </div>
          </div>
          {hasTeamFacts && (
            <div className="flex items-center gap-1 text-blue-400">
              <Info className="w-5 h-5" />
              {isTeamFactsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          )}
        </div>
        {isTeamFactsExpanded && hasTeamFacts && (
          <div className="mt-4 pt-3 border-t border-slate-600">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">Team Facts</h4>
            <ul className="space-y-1">
              {team.facts.map((fact, i) => (
                <li key={`tf-${i}-${fact.slice(0, 20)}`} className="text-slate-300 text-sm flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!team.lineup_submitted ? (
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4 text-center">
          <p className="text-yellow-400 text-sm">Lineup not yet submitted</p>
        </div>
      ) : (
        <>
          {starting.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Starting Lineup
              </h3>
              <div className="space-y-2">
                {starting.map((player, index) => (
                  <PlayerCard
                    key={player.id || index}
                    player={player}
                    teamKey={teamKey}
                    index={index}
                    isExpanded={expandedItems[`${teamKey}-player-${index}`]}
                    onToggle={() => onToggle(`${teamKey}-player-${index}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {substitutes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Substitutes
              </h3>
              <div className="space-y-2">
                {substitutes.map((player, index) => (
                  <PlayerCard
                    key={player.id || index}
                    player={player}
                    teamKey={teamKey}
                    index={`sub-${index}`}
                    isExpanded={expandedItems[`${teamKey}-player-sub-${index}`]}
                    onToggle={() => onToggle(`${teamKey}-player-sub-${index}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Live state hook ----------
// Subscribes to the game's WebSocket and surfaces the latest game snapshot
// plus the most-recent "event" so we can flash a banner / play a sound.
const useLiveGame = (gameId, soundEnabled) => {
  const [liveGame, setLiveGame] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const audioCtxRef = useRef(null);
  const lastEventTimeRef = useRef(0);

  const playSound = useCallback((kind) => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      // Different tones for different event kinds
      const tones = { goal: [660, 880, 1100], card: [220], period: [440, 660], default: [550] };
      const seq = tones[kind] || tones.default;
      seq.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.15;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
        osc.start(start);
        osc.stop(start + 0.42);
      });
    } catch (e) {
      console.warn('Audio playback failed:', e?.message || e);
    }
  }, [soundEnabled]);

  const triggerEvent = useCallback((kind, label, color) => {
    // Debounce: ignore duplicate events fired within 500ms
    const now = Date.now();
    if (now - lastEventTimeRef.current < 500) return;
    lastEventTimeRef.current = now;
    setActiveEvent({ kind, label, color, ts: now });
    playSound(kind);
    setTimeout(() => {
      setActiveEvent(prev => (prev && prev.ts === now) ? null : prev);
    }, 5000);
  }, [playSound]);

  useEffect(() => {
    if (!gameId) return;
    let ws = null;
    let reconnectTimer = null;
    let cancelled = false;
    let prevScore = null;
    let prevCards = null;
    let prevPeriod = null;

    const connect = () => {
      if (cancelled) return;
      const apiUrl = API || '';
      const wsProto = apiUrl.startsWith('https') ? 'wss' : 'ws';
      const host = apiUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProto}://${host}/api/ws/${gameId}`;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) { scheduleReconnect(); return; }
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        const data = msg.data;
        if (!data) return;
        setLiveGame(data);

        const t1 = data.team1 || {};
        const t2 = data.team2 || {};
        const scoreStr = `${t1.score || 0}-${t2.score || 0}|${t1.goals || 0}-${t1.points || 0}|${t2.goals || 0}-${t2.points || 0}|${t1.tries || 0}-${t2.tries || 0}`;
        const cardStr = `${t1.yellow_cards || 0}-${t1.red_cards || 0}-${t1.black_cards || 0}|${t2.yellow_cards || 0}-${t2.red_cards || 0}-${t2.black_cards || 0}`;
        const period = data.period || '';

        // Diff vs previous snapshot (skip the very first 'initial' message)
        if (prevScore !== null) {
          if (scoreStr !== prevScore) {
            triggerEvent('goal', 'SCORE!', 'from-green-500 to-emerald-600');
          } else if (cardStr !== prevCards) {
            triggerEvent('card', 'CARD ISSUED', 'from-yellow-500 to-orange-600');
          } else if (period !== prevPeriod) {
            const labels = {
              firsthalf: 'FIRST HALF',
              halftime: 'HALF TIME',
              secondhalf: 'SECOND HALF',
              fulltime: 'FULL TIME',
              et_firsthalf: 'EXTRA TIME - 1ST',
              et_halftime: 'ET HALF TIME',
              et_secondhalf: 'EXTRA TIME - 2ND',
              penalties: 'PENALTIES',
              pregame: 'PRE-GAME'
            };
            triggerEvent('period', labels[period] || period.toUpperCase(), 'from-blue-500 to-indigo-600');
          }
        }
        prevScore = scoreStr;
        prevCards = cardStr;
        prevPeriod = period;
      };
      ws.onclose = () => { ws = null; scheduleReconnect(); };
      ws.onerror = () => { try { ws && ws.close(); } catch (e) { /* ignore */ } };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, 2000);
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch (e) { /* ignore */ }
    };
  }, [gameId, triggerEvent]);

  return { liveGame, activeEvent };
};

// ---------- Live status banner (sticky top bar) ----------
const LiveStatusBar = ({ liveGame, sport, soundEnabled, onToggleSound }) => {
  const t1 = liveGame?.team1 || {};
  const t2 = liveGame?.team2 || {};
  const period = liveGame?.period || 'pregame';
  const isGAA = sport === 'gaelic_football' || sport === 'hurling';
  const isRugby = sport === 'rugby';

  const formatGAA = (team) => `${team.goals || 0}-${String(team.points || 0).padStart(2, '0')}`;
  const totalGAA = (team) => (team.goals || 0) * 3 + (team.points || 0);

  const periodLabels = {
    pregame: 'Pre-Game',
    firsthalf: '1st Half',
    halftime: 'Half Time',
    secondhalf: '2nd Half',
    fulltime: 'Full Time',
    et_firsthalf: 'ET 1st',
    et_halftime: 'ET HT',
    et_secondhalf: 'ET 2nd',
    penalties: 'Penalties'
  };

  return (
    <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-slate-800" data-testid="live-status-bar">
      <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-red-400 text-xs font-bold uppercase tracking-wider">
            <span className={`w-2 h-2 rounded-full ${liveGame ? 'bg-red-500 animate-pulse' : 'bg-slate-600'}`} />
            {liveGame ? 'Live' : 'Connecting…'}
          </span>
          {liveGame ? (
            <div className="flex items-center gap-2 text-white text-sm font-semibold truncate">
              <span className="truncate">{t1.name || 'Team 1'}</span>
              {isGAA ? (
                <span className="text-blue-300 tabular-nums">
                  {formatGAA(t1)} <span className="text-slate-500 text-xs">({totalGAA(t1)})</span>
                  <span className="text-slate-500 mx-1.5">–</span>
                  {formatGAA(t2)} <span className="text-slate-500 text-xs">({totalGAA(t2)})</span>
                </span>
              ) : isRugby ? (
                <span className="text-blue-300 tabular-nums">{t1.score || 0} – {t2.score || 0}</span>
              ) : (
                <span className="text-blue-300 tabular-nums">{t1.score || 0} – {t2.score || 0}</span>
              )}
              <span className="truncate">{t2.name || 'Team 2'}</span>
            </div>
          ) : (
            <div className="h-4 w-40 bg-slate-800 rounded animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {liveGame && (
            <span className="text-slate-300 text-xs font-medium px-2 py-1 rounded bg-slate-800/60" data-testid="live-period-label">
              {periodLabels[period] || period}
            </span>
          )}
          <button
            type="button"
            onClick={onToggleSound}
            className="text-slate-300 hover:text-white p-1.5 rounded transition-colors"
            title={soundEnabled ? 'Sound on' : 'Sound off'}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Event toast (slides in when an event fires) ----------
const EventBanner = ({ event }) => {
  if (!event) return null;
  const Icon = event.kind === 'goal' ? Goal : event.kind === 'card' ? Square : event.kind === 'period' ? Flag : Clock;
  return (
    <div
      className="fixed top-14 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-[slideDown_0.35s_ease-out]"
      data-testid="event-banner"
    >
      <div className={`bg-gradient-to-r ${event.color} text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-white/20`}>
        <Icon className="w-6 h-6" />
        <span className="font-bold text-lg tracking-wide" data-testid="event-banner-label">{event.label}</span>
      </div>
      <style>{`
        @keyframes slideDown {
          from { transform: translate(-50%, -120%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const Commentator = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [soundEnabled, setSoundEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) { setError('No token provided'); setLoading(false); return; }
      try {
        const response = await axios.get(`${API}/api/commentator/${token}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load match data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const { liveGame, activeEvent } = useLiveGame(data?.game_id, soundEnabled);

  const toggleExpand = (key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading match data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-red-400 text-xl font-semibold mb-2">Unable to Load</h2>
          <p className="text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { team1, team2, competition, venue, match_date, match_time, sport } = data;

  return (
    <div className="min-h-screen bg-slate-900" data-testid="commentator-page">
      <LiveStatusBar
        liveGame={liveGame}
        sport={sport}
        soundEnabled={soundEnabled}
        onToggleSound={() => setSoundEnabled(v => !v)}
      />
      <EventBanner event={activeEvent} />

      <div className="p-4">
        <div className="max-w-4xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-xl p-6 text-center">
            {competition && (
              <p className="text-blue-300 text-sm uppercase tracking-widest mb-2">{competition}</p>
            )}
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {team1.name} vs {team2.name}
            </h1>
            <div className="flex items-center justify-center gap-4 text-slate-400 text-sm">
              {venue && <span>{venue}</span>}
              {(match_date || match_time) && (
                <span>
                  {match_date}{match_date && match_time && ' • '}{match_time}
                </span>
              )}
            </div>
            {!soundEnabled && (
              <button
                type="button"
                onClick={() => setSoundEnabled(true)}
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-xs text-blue-300 hover:text-white bg-blue-900/30 hover:bg-blue-800/50 border border-blue-700/50 rounded-full transition-colors"
                data-testid="enable-sound-btn"
              >
                <BellOff className="w-3.5 h-3.5" />
                Enable event sounds
              </button>
            )}
            {soundEnabled && (
              <p className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-700/40 rounded-full">
                <Bell className="w-3.5 h-3.5" />
                Event sounds on
              </p>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TeamSection
              team={team1}
              teamKey="team1"
              teamName={team1.name}
              expandedItems={expandedItems}
              onToggle={toggleExpand}
            />
            <TeamSection
              team={team2}
              teamKey="team2"
              teamName={team2.name}
              expandedItems={expandedItems}
              onToggle={toggleExpand}
            />
          </div>

          <div className="mt-8 bg-slate-800/30 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-400" />
              Quick Reference
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-400">
              <div>
                <p>Tap on any <span className="text-blue-400">highlighted</span> player or team name to reveal facts and notes.</p>
              </div>
              <div>
                <p>The bar at the top updates live with the score, period and event alerts.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-slate-500 text-xs">
            <p>Commentator View • Read-Only</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commentator;
